import { useCallback, useEffect, useRef, useState } from "react";
import { useGetLogoHook } from "../types/TenantLogo";
import { getMimeType } from "../utilities/getMimeType";

type UseTenantLogoParams = {
  id?: string;
  useGetLogo: useGetLogoHook;
};

type UseTenantLogoResult = {
  isLoading: boolean;
  logoUrl: string | undefined;
};

export function useTenantLogo({
  id,
  useGetLogo,
}: UseTenantLogoParams): UseTenantLogoResult {
  const [logoUrl, setLogoUrl] = useState<string | undefined>(undefined);
  const logoObjectUrlRef = useRef<string | null>(null);

  const { data, isLoading, isError } = useGetLogo({ id });

  const revokeLogoObjectUrl = useCallback(() => {
    if (logoObjectUrlRef.current) {
      URL.revokeObjectURL(logoObjectUrlRef.current);
      logoObjectUrlRef.current = null;
    }
  }, []);

  useEffect(() => {
    revokeLogoObjectUrl();
    setLogoUrl(undefined);
  }, [id, revokeLogoObjectUrl]);

  useEffect(() => {
    return () => {
      revokeLogoObjectUrl();
    };
  }, [revokeLogoObjectUrl]);

  useEffect(() => {
    if (isLoading) return;

    if (isError || !data) {
      setLogoUrl(undefined);
      revokeLogoObjectUrl();
      return;
    }

    if (data.contentType?.startsWith("text/plain") === true) {
      setLogoUrl(undefined);
      revokeLogoObjectUrl();
      return;
    }

    const mimeType = getMimeType(data.body, data.contentType);
    if (!mimeType?.startsWith("image/")) {
      setLogoUrl(undefined);
      revokeLogoObjectUrl();
      return;
    }

    const blob = new Blob([data.body], { type: mimeType });
    revokeLogoObjectUrl();
    const imgUrl = URL.createObjectURL(blob);
    logoObjectUrlRef.current = imgUrl;
    setLogoUrl(imgUrl);
  }, [isLoading, isError, data, revokeLogoObjectUrl]);

  return {
    isLoading,
    logoUrl,
  };
}
